import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import AgentMetadata from '../metadata/agent_metadata';
import ApiMetadata from '../metadata/api_metadata';
import Communicator from 'models/communicator';
import connect from 'components/lib/connect';
import ConversationItem from 'models/conversation_item';
import ItemContent from '../item_content';
import Metadata from '../metadata/metadata';
import PrepositionalPhrase from '../metadata/prepositional_phrase';
import RuleLink from '../metadata/rule_link';

export function TopicChangeItem({ addedTopics, item, removedTopics }) {
  const SIDEKICK_SYSTEM_IDS = new Set(['SIDEKICK', 'SIDEKICK_V2']);
  const SIDEKICK_APP_NAME = 'Sidekick';
  const isSidekick = item.initiator.type === Communicator.SYSTEM && SIDEKICK_SYSTEM_IDS.has(item.initiator.id);
  const isApiInitiator = item.initiator.type === Communicator.API || isSidekick;
  const appName = isSidekick ? SIDEKICK_APP_NAME : undefined;
  const content = getTopicChangeContent();

  return (
    <ItemContent isAuditItem item={item}>
      {content}
    </ItemContent>
  );

  function getTopicChangeContent() {
    if (item.initiator.type === Communicator.AGENT) {
      return getAgentTopicChangeContent();
    }

    if (isApiInitiator) {
      return getApiTopicChangeContent();
    }

    return getRuleTopicChangeContent();
  }

  function getAgentTopicChangeContent() {
    return (
      <>
        {addedTopics.length > 0 ? (
          <TopicChangeDisplayItem data-aid="conversationTopicAdded">
            <AgentMetadata
              item={item}
              object={addedTopics.join(', ')}
              text={`added topic${addedTopics.length > 1 ? 's' : ''} `}
            />
          </TopicChangeDisplayItem>
        ) : null}
        {removedTopics.length > 0 ? (
          <TopicChangeDisplayItem data-aid="conversationTopicRemoved">
            <AgentMetadata
              item={item}
              object={removedTopics.join(', ')}
              text={`removed topic${removedTopics.length > 1 ? 's' : ''} `}
            />
          </TopicChangeDisplayItem>
        ) : null}
      </>
    );
  }

  function getRuleTopicChangeContent() {
    return (
      <>
        {addedTopics.length > 0 ? (
          <TopicChangeDisplayItem data-aid="conversationTopicAdded">
            <Metadata
              isInbound={false}
              prepositionalPhrases={getNonAgentTopicPrepositionalPhrases(addedTopics, 'added')}
              timestamp={item.timestamp}
            />
          </TopicChangeDisplayItem>
        ) : null}
        {removedTopics.length > 0 ? (
          <TopicChangeDisplayItem data-aid="conversationTopicRemoved">
            <Metadata
              isInbound={false}
              prepositionalPhrases={getNonAgentTopicPrepositionalPhrases(removedTopics, 'removed')}
              timestamp={item.timestamp}
            />
          </TopicChangeDisplayItem>
        ) : null}
      </>
    );
  }

  function getApiTopicChangeContent() {
    return (
      <>
        {addedTopics.length > 0 ? (
          <TopicChangeDisplayItem data-aid="conversationTopicAdded">
            <ApiMetadata
              appName={appName}
              item={item}
              prepositionalPhrases={getNonAgentTopicPrepositionalPhrases(addedTopics, 'added')}
            />
          </TopicChangeDisplayItem>
        ) : null}
        {removedTopics.length > 0 ? (
          <TopicChangeDisplayItem data-aid="conversationTopicRemoved">
            <ApiMetadata
              appName={appName}
              item={item}
              prepositionalPhrases={getNonAgentTopicPrepositionalPhrases(removedTopics, 'removed')}
            />
          </TopicChangeDisplayItem>
        ) : null}
      </>
    );
  }

  function getNonAgentTopicPrepositionalPhrases(topics, action) {
    const prepositionalPhrases = [];

    prepositionalPhrases.push(
      <PrepositionalPhrase
        complement={topics.join(', ')}
        key="topic"
        preposition={`Topic${topics.length > 1 ? 's' : ''}`}
      />
    );

    let complement = '';
    let preposition = `${action}`;
    if (!isApiInitiator) {
      complement = <RuleLink ruleId={item.initiator.id}>rule</RuleLink>;

      preposition = `${preposition} by`;
    }

    prepositionalPhrases.push(<PrepositionalPhrase complement={complement} key="by" preposition={preposition} />);
    return prepositionalPhrases;
  }
}

TopicChangeItem.propTypes = {
  addedTopics: PropTypes.arrayOf(String).isRequired,
  item: PropTypes.instanceOf(ConversationItem).isRequired,
  removedTopics: PropTypes.arrayOf(String).isRequired,
};

const TopicChangeDisplayItem = styled.div`
  align-items: flex-end;
  display: flex;
  flex-direction: column;
  max-width: 100%;
`;

function mapStateToProps({ getProvider }, { item }) {
  let addedTopics = _.sortBy(getTopicNames(item.content.addedTopics), topic => topic.toLowerCase());
  let removedTopics = _.sortBy(getTopicNames(item.content.removedTopics), topic => topic.toLowerCase());

  return {
    addedTopics,
    item,
    removedTopics,
  };

  function getTopicNames(topicIds) {
    let topicNames = [];
    topicIds.forEach(topicId => {
      let topic = getProvider('topics').findBy({ id: topicId });
      if (topic) {
        topicNames.push(topic.getNameWithAncestry());
      }
    });
    return topicNames;
  }
}

const TopicChangeItemContainer = connect(mapStateToProps, null)(TopicChangeItem);
TopicChangeItemContainer.propTypes = {
  item: PropTypes.instanceOf(ConversationItem).isRequired,
};

export default TopicChangeItemContainer;
